import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Box } from '@mui/system';
import { Paper, Typography } from '@mui/material';

import {
  ITEMS_PER_PAGE,
  TypeOfUser,
  activeUserList,
  getActiveUsers,
  logos,
  selectActiveUsersSegment,
  setActiveUsersSegment,
  activeUserCount,
  successSelector,
  adminActions,
} from '@pharmaplan/common';

import useWindowDimensions from '../../../hooks/useWindowDimensions';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import strings from '../../../localization';

import ActiveUsersSegments from './ActiveUsersSegments';
import ActiveUserItem from './ActiveUserItem';
import styles from './styles';

const segments = () =>
  [
    { key: TypeOfUser.pharmacist, label: strings.professionals },
    { key: TypeOfUser.pharmacy, label: strings.pharmacies },
  ];

const ActiveUsers = () => {
  const dispatch = useAppDispatch();
  const allLogos = useAppSelector(logos);
  const success = useAppSelector((state) =>
    successSelector([adminActions.getActiveUsers], state));
  const segment = useAppSelector(selectActiveUsersSegment);
  const activeCount = useAppSelector(activeUserCount);
  const { height } = useWindowDimensions();

  const [page, setPage] = useState(1);
  const listEndRef = useRef(null);
  const activeUsers = useAppSelector(activeUserList);

  const handleSegmentClick = (seg: TypeOfUser) =>
    () => {
      dispatch(setActiveUsersSegment(seg));
    };

  useEffect(() => {
    dispatch(getActiveUsers({ page, user: segment }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segment, page]);

  const observerCallback = useCallback(
    (entries: { isIntersecting: boolean }[]) => {
      const paginationCondition = activeUsers.length >= page * ITEMS_PER_PAGE;

      if (paginationCondition && entries[0].isIntersecting) {
        setPage((prevPage) =>
          prevPage + 1);
      }
    },
    [page, activeUsers],
  );

  useEffect(() => {
    const observer = new IntersectionObserver(observerCallback, {
      root: null,
      rootMargin: '0px',
      threshold: 1,
    });

    if (listEndRef.current) {
      observer.observe(listEndRef.current);
    }

    return () => {
      if (listEndRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        observer.unobserve(listEndRef.current);
      }
    };
  }, [observerCallback]);

  return (
    <Box component={Paper} sx={[styles.container]}>
      <Typography sx={styles.title}>{strings.connectedUsers}</Typography>

      <ActiveUsersSegments
        tabs={segments().map(({ key, label }) =>
          ({
            key,
            label,
            count: key === segment ? activeUsers.length : activeCount,
          }))}
        selected={segment}
        showCount={success}
        onSelect={handleSegmentClick}
      />

      <Box sx={[styles.list, styles.dynamicHeight(height ?? 1)]}>
        {activeUsers.map((item) => {
          const { id, logoId } = item ?? {};
          const { image } = allLogos.find((it) =>
            it.logoId === logoId) ?? {};

          return (
            <ActiveUserItem
              segment={segment}
              logo={image}
              key={id}
              item={item}
            />
          );
        })}
        <Box component="div" ref={listEndRef} />
      </Box>
    </Box>
  );
};

export default ActiveUsers;
